<script setup lang="ts">
  import SettingsPage from "@/views/admin/settings/settings-page.vue";
  import { useCreatePartner, useFetchPartner, useUpdatePartner } from "@/data/hooks/partners";
  import { useRoute, useRouter } from "vue-router";
  import { TextInput, SelectInput, MultiSelect } from "@/ui";
  import { PartnerBody, PartnerType } from "@/data/actions-query";
  import SectionTitle from "./components/section-title.vue";
  import ServicesArray from "./components/services-array.vue";
  import { useForm } from "vee-validate";
  import { array, mixed, number, object, string } from "yup";
  import { toTypedSchema } from "@vee-validate/yup";
  import { useFetchOffices } from "@/data/hooks/offices";
  import { computed, watch } from "vue";
  import Button from "primevue/button";
  import { useI18n } from "vue-i18n";
  import { useToast } from "vue-toast-notification";
  import store from "@/store";
  import parsePhoneNumberFromString from "libphonenumber-js";

  const route = useRoute();
  const router = useRouter();
  const partnerId = computed(() => (route.params.id ? Number(route.params.id) : undefined));
  const editMode = computed(() => partnerId.value !== undefined);
  const { t } = useI18n();
  const toast = useToast();

  const { group_id } = store.getters["auth/me"];
  const { data: offices } = useFetchOffices(group_id);

  const typeOptions = [
    PartnerType.photo,
    PartnerType.epcInspector,
    PartnerType.electricityInspector,
    PartnerType.fuelOilTankInspector,
    PartnerType.asbestosInspector,
    PartnerType.stylist,
    PartnerType.copywriter,
  ];
  const partnerTypes = typeOptions.map((type) => ({
    label: t(`views.partner.${type}_title`),
    value: type,
  }));

  const officeOptions = computed(() => {
    return (offices?.value ?? []).map((office) => ({
      label: office.name,
      value: office.id,
    }));
  });

  const { handleSubmit, resetForm, meta, isSubmitting } = useForm({
    validationSchema: toTypedSchema(
      object({
        company: string().required("Bedrijfsnaam is verplicht."),
        type: mixed<PartnerType>().oneOf(Object.values(PartnerType)).required("Type is verplicht."),
        officeIds: array().of(number().required()).required("Kantoren zijn verplicht."),
        account: object({
          first_name: string().required("Voornaam is verplicht."),
          last_name: string().required("Achternaam is verplicht."),
          email: string().email().required("Email is verplicht."),
          phone: string()
            .test("is-valid-phone", "Ongeldig telefoonnummer", (value) => {
              if (!value) return true; // allow empty (optional)
              const phone = parsePhoneNumberFromString(value, { defaultCountry: "BE", extract: false });
              return phone && phone.isValid();
            })
            .required("Telefoon is verplicht."),
        }),
        services: array()
          .of(
            object({
              service: string().required("Dienst is verplicht."),
              products: array().of(string().required("Product is verplicht.")).required(),
            }),
          )
          .min(1, "At least one service is required"),
      }),
    ),
    initialValues: {
      services: [
        {
          service: "",
          products: [],
        },
      ],
    },
  });

  // Edit mode
  const { data: partner, isLoading } = useFetchPartner(partnerId);
  watch(
    partner,
    () => {
      if (partner.value && !meta.value.dirty) {
        const contactPerson = partner.value.accounts?.at(-1) ?? partner.value.invites?.at(-1); // TODO: this is a hack, changing doesn't properly work!!!
        resetForm({
          values: {
            company: partner.value.company_name,
            type: partner.value.type,
            account: {
              first_name: contactPerson?.first_name,
              last_name: contactPerson?.last_name,
              email: contactPerson?.email,
              phone: contactPerson?.phone,
            },
            officeIds: partner.value.offices?.map((office) => office.id),
            services: partner.value.services?.map((service) => ({
              service: service.title,
              products: service.addons?.map((addon) => addon.title) ?? [],
            })),
          },
        });
      }
    },
    { immediate: true },
  );

  const { mutateAsync: createPartner } = useCreatePartner();
  const { mutateAsync: updatePartner } = useUpdatePartner();

  const onSubmit = handleSubmit(async (values) => {
    const payload: PartnerBody = {
      company_name: values.company,
      office_ids: values.officeIds,
      type: values.type,
      services: values.services?.map((service) => ({
        title: service.service,
        addons: service.products?.map((product) => ({ title: product })),
      })),
      accounts: [values.account],
    };
    try {
      if (editMode.value) {
        await updatePartner({
          id: partnerId.value!,
          payload,
        });
        toast.success("Partner succesvol aangepast");
      } else {
        await createPartner(payload);
        toast.success("Partner succesvol aangemaakt");
      }
      router.push({ name: "admin-partners" });
    } catch (error) {
      toast.error(t("errors.generic.error"));
      console.error(error);
    }
  });
</script>

<template>
  <div class="flex flex-col h-full justify-between">
    <SettingsPage :title="partner?.company_name ? `Partner aanpassen` : 'Partner uitnodigen'" :is-loading="isLoading" isWithBackButton>
      <div class="flex flex-col gap-10">
        <div class="flex flex-col gap-4">
          <section-title title="Bedrijfsgegevens" />
          <div class="flex flex-row gap-6 lg:w-3/4">
            <TextInput class="flex-1" name="company" label="Bedrijfsnaam" required />
            <SelectInput class="flex-1" name="type" label="Type" :options="partnerTypes" optionValue="value" optionLabel="label" required />
          </div>
          <MultiSelect class="mt-2 lg:w-3/4" name="officeIds" label="Kantoren" :options="officeOptions" optionValue="value" optionLabel="label" required />
        </div>
        <div class="flex flex-col gap-4">
          <section-title title="Contactpersoon" />
          <div class="flex flex-row gap-6 lg:w-3/4">
            <TextInput class="flex-1" name="account.first_name" label="Voornaam" required />
            <TextInput class="flex-1" name="account.last_name" label="Achternaam" required />
          </div>
          <div class="flex flex-row gap-6 lg:w-3/4 mt-2">
            <TextInput class="flex-1" name="account.email" label="Email" required />
            <TextInput class="flex-1" name="account.phone" label="Telefoon" required />
          </div>
        </div>
        <div class="flex flex-col gap-4">
          <section-title title="Diensten" />
          <services-array class="lg:w-3/4" />
        </div>
      </div>
    </SettingsPage>
    <div class="flex flex-row gap-2 sticky md:bottom-0 bottom-[94px] bg-white px-6 py-2 border border-gray-300">
      <Button :label="editMode ? 'Opslaan' : 'Verstuur uitnodiging'" :loading="isSubmitting" :disabled="isSubmitting" @click="onSubmit" />
      <Button label="Annuleer" outlined @click="router.push({ name: 'admin-partners' })" />
    </div>
  </div>
</template>

<style scoped>
  :deep(.p-multiselect) {
    min-width: 17rem;
  }
  :deep(.p-multiselect-label) {
    display: flex;
    flex-wrap: wrap;
  }
  :deep(.p-multiselect-token) {
    margin: 0.1rem;
  }
</style>
